import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";

import {
  BarOptionDefinition,
  CountText,
  Loader,
  MenuOptionSpec,
  NotificationType,
  Toolbar,
} from "@transfr-inc/dashboard-components";
import { getLearnerTerm } from "@transfr-inc/dashboard-components/constants";
import { Button } from "@transfr-inc/dashboard-components/forms";
import {
  PageHeader,
  PageLayout,
} from "@transfr-inc/dashboard-components/layouts";

// View components
import { OrganizationFilter } from "@components/custom/forms";
import { UpdatePasswordSuccess } from "@views/classrooms/details/components/menu-builder/utils/notification.constants";
import { UsersTabs } from "./users.tabs";

// Modals
import { BulkArchiveUserModal } from "@components/common/bulk-archive-user-modal";
import { DeleteUserModal } from "@components/common/delete-user-modal";
import { NewUserModal } from "@views/users/new-user/new-user-modal.jsx";

import {
  AddTraineeClassroomModal,
  ChangePasswordModal,
  EditUserModal,
} from "@components/modals";
import ResendInviteModal from "./resend-invite-modal";

// Utils
import { RecordStatus } from "@components/custom/dropdowns";
import { useNotificationContext } from "@contexts/notification-context";
import {
  trackLearnersArchived,
  trackLearnersReactivated,
} from "@lib/event-trackers";
import { AllRoles, Role } from "@models/role";
import {
  useArchiveUserEnabled,
  useArchiveUserVisible,
  useCurrentUser,
  useDeleteUserAllowed,
  useFeatures,
  useIntegrationOrgCodes,
  useRole,
} from "@queries";
import { useOrganizationUsers } from "@queries/organization";
import { useContainer } from "@src/contexts/container-context";

import "./index.scss";

const getRoleMap = (data) => {
  const roleMap = new Map();

  for (const user of data) {
    if (!roleMap.has(user.roleId)) {
      roleMap.set(user.roleId, []);
    }

    const userArray = roleMap.get(user.roleId);
    userArray.push(user);
  }

  for (const role of AllRoles) {
    if (!roleMap.has(role.id)) {
      roleMap.set(role.id, []);
    }
  }
  return roleMap;
};

export default function UserList({ responsiveStyles = {}, className = "" }) {
  // For MVP we will retrieve all of the users at once and not refresh a clicked tab. The user will need to reload
  // the page in order to see updated data in any of the tabs
  const originalDataRef = useRef();
  const organizationFilterRef = useRef();

  const [roleMap, setRoleMap] = useState(new Map());
  const [stateShowEditDetails, setStateShowEditDetails] = useState(false);
  const [stateUser, setStateUser] = useState();
  const [openPasswordModal, setOpenPasswordModal] = useState();
  const [openResendModal, setOpenResendModal] = useState();
  const [openAddToClassroom, setOpenAddToClassroom] = useState();
  const [dataFiltered, setDataFiltered] = useState();

  const role = useRole();
  const features = useFeatures();
  const currentUser = useCurrentUser();
  const queryClient = useQueryClient();
  const { userId, organizationCode } = currentUser || {};

  const archiveUserEnabled = useArchiveUserEnabled(organizationCode);
  const archiveUserVisible = useArchiveUserVisible();
  const deleteUserAllowed = useDeleteUserAllowed(organizationCode);
  // Multi-org lists span child orgs; gate each row by its own org.
  const { isIntegrationOrgCode } = useIntegrationOrgCodes(organizationCode);

  const [statusFilter, setStatusFilter] = useState(RecordStatus.Active);

  const { data: users, isFetching: isFetchingUsers } = useOrganizationUsers(
    { role, orgCode: organizationCode, userId, status: statusFilter },
    {
      enabled: !!(role && organizationCode && userId),
      placeholderData: undefined,
    }
  );

  const { addNotification } = useNotificationContext();
  const { userService, classroomService } = useContainer();

  const getUsers = () => {
    queryClient.invalidateQueries({
      queryKey: ["organizationUsers", role, organizationCode, userId],
    });
  };

  const onOrganizationChange = (option) => {
    const originalData = originalDataRef.current;

    let filteredData = [];
    if (option) {
      filteredData = originalData.filter(
        (value) => value.organizationCode == option.code
      );
      setDataFiltered(true);
    } else {
      filteredData = originalData;
      setDataFiltered(false);
    }

    organizationFilterRef.current = option;
    setRoleMap(getRoleMap(filteredData));
  };

  const [show, setShow] = useState(false);

  const onSetShow = () => {
    setShow(true);
  };

  const [showDelete, setShowDelete] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [archiveList, setArchiveList] = useState([]);
  const [selectedUserList, setSelectedUserList] = useState([]);
  const [addToClassroomList, setAddToClassroomList] = useState([]);

  const refreshUsersData = () => {
    const organizationSelected = organizationFilterRef.current;
    originalDataRef.current = users;
    onOrganizationChange(organizationSelected);
  };

  useEffect(() => {
    if (users) {
      refreshUsersData();
    }
  }, [users]);

  const toolbar = (
    <Toolbar>
      {features.canCreateUser && role != Role.INSTRUCTOR.id && (
        <Button
          icon={["fa-regular", "user-plus"]}
          primary
          title={"Create User"}
          tooltip={"Create User"}
          onClick={() => setShow(true)}
          size="small"
        ></Button>
      )}
    </Toolbar>
  );

  const onDelete = (usersToDelete) => {
    setSelectedUserList(usersToDelete);
    setShowDelete(true);
  };

  const onArchiveUsers = (usersToAction) => {
    const targets = (usersToAction ?? []).filter((u) => !u.isArchived);
    if (targets.length === 0) {
      return;
    }

    setArchiveList(targets);
    setShowArchive(true);
  };

  const onUnarchiveUsers = async (usersToAction) => {
    const targets = (usersToAction ?? []).filter((u) => u.isArchived);
    if (targets.length === 0) {
      return;
    }

    try {
      await userService.unarchiveUsers(
        targets.map((u) => u.userId),
        targets[0]?.organizationCode
      );
      trackLearnersReactivated(targets, { batchId: crypto.randomUUID() });
      launchNotification({
        type: NotificationType.success,
        message:
          targets.length > 1 ? (
            <div>
              <b>{targets.length}</b> {getLearnerTerm({ plural: true })} have
              been unarchived.
            </div>
          ) : (
            <div>
              <b>
                {targets[0].firstName} {targets[0].lastName}
              </b>{" "}
              has been unarchived.
            </div>
          ),
        icon: ["fa-solid", "circle-check"],
      });
      setSelectedUserList([]);
      await queryClient.refetchQueries({
        queryKey: ["organizationUsers", role, organizationCode, userId],
      });
    } catch (error) {
      console.log(error);
      launchNotification({
        type: NotificationType.error,
        message: "Something went wrong. Please try again.",
      });
    }
  };

  const onArchiveUser = async (keys, data) => {
    onArchiveUsers([data]);
  };

  const onUnarchiveUser = async (keys, data) => {
    await onUnarchiveUsers([data]);
  };

  const onUpdatePassword = (usersToUpdate) => {
    setSelectedUserList(usersToUpdate);
    setOpenPasswordModal(true);
  };

  const onRowRemoved = () => {
    setSelectedUserList([]);
    getUsers();
  };

  const onPasswordUpdatedHandler = (response) => {
    setOpenPasswordModal();
    onRowRemoved();
    launchNotification({
      type: NotificationType.success,
      message: UpdatePasswordSuccess(response),
      icon: ["fa-solid", "circle-check"],
    });
  };

  const onInvitationSentHandler = () => {
    setOpenResendModal();
    launchNotification({
      type: NotificationType.success,
      message: (
        <div>
          An account creation email has been sent to
          <b>
            {" "}
            {stateUser.firstName} {stateUser.lastName}
          </b>
        </div>
      ),
      icon: ["fa-solid", "circle-check"],
    });
  };

  const onCloseChangePassword = () => {
    setOpenPasswordModal();
  };

  const onCloseResendInvite = () => {
    setOpenResendModal();
  };

  const onCloseEditDetails = () => {
    setStateShowEditDetails(false);
  };

  const onUserCreated = async (notification) => {
    launchNotification(notification);
    notification && getUsers();
  };

  const launchNotification = (notification) => {
    addNotification(notification);
  };

  const onUpdateUser = async (user) => {
    try {
      const updatedUser = await userService.updateUserMetadata(user);
      launchNotification({
        type: NotificationType.success,
        message: `User ${updatedUser.firstName} ${updatedUser.lastName} has been updated successfully.`,
        icon: ["fa-solid", "circle-check"],
      });

      onCloseEditDetails();
      getUsers();
      return updatedUser;
    } catch (error) {
      console.log(error);
      const { data } = error;
      user.error =
        data?.detail ?? "Something went wrong, please try again later.";
      return user;
    }
  };

  const onDeleteUser = async (keys, data) => {
    onDelete([data]);
  };

  const onEditDetails = async (keys, data) => {
    const user = await userService.getUserDetails(data.userId);
    setStateUser(user);
    setStateShowEditDetails(true);
  };

  const onChangePassword = async (keys, data) => {
    setSelectedUserList([data]);
    setOpenPasswordModal(true);
  };

  const onResendInvite = async (keys, data) => {
    setStateUser(data);
    setOpenResendModal(true);
  };

  const onOpenAddToClassroom = async (keys, data) => {
    data ? setAddToClassroomList([data]) : setAddToClassroomList(keys);
    setOpenAddToClassroom(true);
  };

  const onAddTraineeToClassroom = async (classroom) => {
    const { classroomId, name } = classroom;
    for (const t of addToClassroomList) {
      await classroomService.addTrainee(classroomId, t.userId);
    }

    setOpenAddToClassroom();
    getUsers();
    launchNotification({
      type: NotificationType.success,
      message:
        addToClassroomList.length == 1 ? (
          <>
            <strong>{`${addToClassroomList[0].firstName} ${addToClassroomList[0].lastName}`}</strong>{" "}
            has been added to classroom <strong>{name}</strong>
          </>
        ) : (
          <>
            <strong>{`${addToClassroomList.length} ${getLearnerTerm({
              plural: true,
            })}`}</strong>{" "}
            have been added to classroom <strong>{name}</strong>
          </>
        ),
      icon: ["fa-solid", "circle-check"],
    });
  };

  const bottomBarOptions = {
    addToClassroom: new BarOptionDefinition(onOpenAddToClassroom, {
      text: "Add to Classroom",
      icon: ["fa-light", "screen-users"],
      destructive: false,
    }),
    updatePasswords: new BarOptionDefinition(onUpdatePassword, {
      text: "Change Password",
      icon: ["fa-light", "fa-key"],
      destructive: false,
    }),
    archive: new BarOptionDefinition(onArchiveUsers, {
      text: "Archive",
      icon: ["fa-light", "box-archive"],
      destructive: false,
    }),
    unarchive: new BarOptionDefinition(onUnarchiveUsers, {
      text: "Unarchive",
      icon: ["fa-light", "box-open"],
      destructive: false,
    }),
    delete: new BarOptionDefinition(onDelete, {
      text: "Delete",
      icon: ["fa-light", "fa-trash-can"],
      destructive: true,
    }),
  };

  const actionMenuOptions = {
    delete: new MenuOptionSpec(
      "Delete",
      ["fa-light", "fa-trash-can"],
      onDeleteUser,
      "delete"
    ),
    edit: new MenuOptionSpec(
      "Edit Details",
      ["fa-light", "fa-edit"],
      onEditDetails,
      "edit"
    ),
    changePassword: new MenuOptionSpec(
      "Change Password",
      ["fa-light", "fa-key"],
      onChangePassword,
      "change_password"
    ),
    resendInvite: new MenuOptionSpec(
      "Resend Invite",
      ["fa-light", "fa-paper-plane"],
      onResendInvite,
      "resend_invite"
    ),
    addToClassroom: new MenuOptionSpec(
      "Add to Classroom",
      ["fa-light", "screen-users"],
      onOpenAddToClassroom,
      "add_to_classroom"
    ),
    archive: new MenuOptionSpec(
      "Archive",
      ["fa-light", "box-archive"],
      onArchiveUser,
      "archive"
    ),
    unarchive: new MenuOptionSpec(
      "Unarchive",
      ["fa-light", "box-open"],
      onUnarchiveUser,
      "unarchive"
    ),
  };

  const orgFilter = (role === Role.MULTI_ORG_ADMIN.id ||
    role === Role.MULTI_ORG_VIEWER.id) && (
    <OrganizationFilter
      orgCode={organizationCode}
      onOrganizationSelected={onOrganizationChange}
    />
  );

  const pageHeader = (
    <PageHeader
      title={"Users"}
      badge={isFetchingUsers ? "..." : <CountText count={users?.length} />}
      middlePane={orgFilter}
    >
      {toolbar}
    </PageHeader>
  );

  return (
    <PageLayout
      className={clsx("users-page", className)}
      header={pageHeader}
      responsiveStyles={responsiveStyles}
    >
      {isFetchingUsers && <Loader overlay fixed />}
      <UsersTabs
        data={roleMap}
        responsiveStyles={responsiveStyles}
        bottomBarOptions={bottomBarOptions}
        actionMenuOptions={actionMenuOptions}
        onSetShowNewUser={onSetShow}
        dataFiltered={dataFiltered}
        creatableUsers={features.creatableUsers}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        archiveUserEnabled={archiveUserEnabled}
        showStatusDropdown={archiveUserVisible}
        deleteUserAllowed={deleteUserAllowed}
        isIntegrationOrgCode={isIntegrationOrgCode}
      />
      {features.canCreateUser && show && (
        <NewUserModal
          show={show}
          setShow={() => setShow()}
          role={role}
          onUserCreated={onUserCreated}
          {...features}
        />
      )}
      <DeleteUserModal
        onCancel={() => setShowDelete()}
        show={showDelete}
        deleteUserList={selectedUserList}
        onRowRemoved={onRowRemoved}
      />
      <BulkArchiveUserModal
        open={showArchive}
        learners={archiveList}
        onCancel={() => setShowArchive(false)}
        onArchived={() => {
          trackLearnersArchived(archiveList, {
            source: "tab",
            batchId: crypto.randomUUID(),
          });
          setArchiveList([]);
          getUsers();
        }}
      />
      {stateShowEditDetails && stateUser && (
        <EditUserModal
          open={stateShowEditDetails}
          onUpdateUser={onUpdateUser}
          onClose={onCloseEditDetails}
          user={stateUser}
        />
      )}
      <ChangePasswordModal
        open={openPasswordModal}
        userIds={selectedUserList.map((u) => u.userId)}
        onCancel={onCloseChangePassword}
        onPasswordChanged={onPasswordUpdatedHandler}
      ></ChangePasswordModal>
      <ResendInviteModal
        open={openResendModal}
        userId={stateUser?.userId}
        onClose={onCloseResendInvite}
        onInvitationSent={onInvitationSentHandler}
      ></ResendInviteModal>
      <AddTraineeClassroomModal
        open={openAddToClassroom}
        orgCode={organizationFilterRef.current?.code}
        onCancel={() => setOpenAddToClassroom()}
        onAddTrainee={onAddTraineeToClassroom}
        bulk
        async
      ></AddTraineeClassroomModal>
    </PageLayout>
  );
}
